import { useMemo, useState } from "react";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import {
  TDistributorType,
  TDistributorTypeForm,
  TDistributorTypeFilter,
  DistributorTypeForm,
  DistributorTypeTable,
  DistributorTypeHeader,
  useDistributorTypes,
  useSaveDistributorType,
  useDistributorTypeStatusToggle,
  distributorTypeSchema,
} from "../../features/distributortypes";
import { useIsAuth } from "../../utils/useAuth";

const filterInit: TDistributorTypeFilter = {};

const DistributorTypesPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "createdAt",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TDistributorTypeFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, name: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { distributorTypes, paginationData, isLoading } = useDistributorTypes({
    filter,
    pagination,
  });
  const { handleSave } = useSaveDistributorType();
  const { handleEnable, handleDisable } = useDistributorTypeStatusToggle();

  const { userData } = useIsAuth();

  const formDataInit: TDistributorTypeForm = useMemo(() => {
    return {
      name: "",
      keyValue: "",
      createdBy: userData?.userId || "",
      active: true,
    };
  }, [userData?.userId]);

  const formik = useFormik<TDistributorTypeForm>({
    initialValues: formDataInit,
    validationSchema: distributorTypeSchema,
    onSubmit: (
      values: TDistributorTypeForm,
      formikHelpers: FormikHelpers<TDistributorTypeForm>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm();
      handleClose();
    },
  });

  const handleOpenNew = () => {
    formik.resetForm();
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    formik.resetForm();
  };

  const handleEdit = (distributorType: TDistributorType) => {
    formik.setValues({
      _id: distributorType._id,
      name: distributorType.name,
      keyValue: distributorType.keyValue,
      active: distributorType.active,
    });
    setShowModal(true);
  };

  return (
    <div>
      <DistributorTypeHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNew} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <DistributorTypeTable
        data={distributorTypes}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof TDistributorType}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: (row) => handleEdit(row),
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id),
            shouldShow: (row) => row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleClose} submit={formik.handleSubmit}>
        <DistributorTypeForm formik={formik} />
      </Modal>
    </div>
  );
};

export default DistributorTypesPage;
